import { useState } from "react";
import { Box, Tooltip, IconButton, Avatar, Divider, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import Check from "@mui/icons-material/Check";
import { Link } from "@tanstack/react-router";
import Inventory from "@mui/icons-material/Inventory";
import Settings from "@mui/icons-material/Settings";
import People from "@mui/icons-material/People";
import Payment from "@mui/icons-material/Payment";
import AdminPanelSettings from "@mui/icons-material/AdminPanelSettings";
import Download from "@mui/icons-material/Download";
import Help from "@mui/icons-material/Help";
import Person from "@mui/icons-material/Person";
import Logout from "@mui/icons-material/Logout";
import Translate from "@mui/icons-material/Translate";
import Webhook from "@mui/icons-material/Webhook";
import { Organization } from "@/types/Organization";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/useLanguage";

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  to: string;
  isActive: boolean;
}

function NavItem({ icon, label, to, isActive }: NavItemProps) {
  return (
    <Tooltip title={label} placement="right" arrow>
      <Box
        component={Link}
        to={to as "/projects"}
        aria-current={isActive ? "page" : undefined}
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: isActive ? "primary.main" : "rgba(156, 163, 175, 0.9)",
          backgroundColor: isActive
            ? "rgba(59, 130, 246, 0.15)"
            : "transparent",
          borderLeft: isActive ? "3px solid" : "3px solid transparent",
          borderLeftColor: isActive ? "primary.main" : "transparent",
          transition: "all 0.2s ease",
          textDecoration: "none",
          "&:hover": {
            backgroundColor: isActive
              ? "rgba(59, 130, 246, 0.2)"
              : "rgba(255, 255, 255, 0.08)",
            color: isActive ? "primary.main" : "#fff",
          },
        }}
      >
        {icon}
      </Box>
    </Tooltip>
  );
}

interface IconSidebarProps {
  user:
    | {
        full_name?: string;
        email?: string;
        avatar_url?: string;
        is_superuser?: boolean;
      }
    | null
    | undefined;
  currentOrganization: Organization | null;
  onOrgMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  currentPath: string | undefined;
}

export default function IconSidebar({
  user,
  currentOrganization,
  onOrgMenuOpen,
  currentPath,
}: IconSidebarProps) {
  const { t } = useTranslation("nav");
  const { currentLanguage, changeLanguage } = useLanguage();
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);

  const languages = [
    { code: "en" as const, label: "English" },
    { code: "fr" as const, label: "Français" },
  ];

  const isActive = (path: string, exact = false) => {
    if (!currentPath) return false;
    return exact ? currentPath === path : currentPath.startsWith(path);
  };

  const avatarUrl = user?.avatar_url
    ? `${import.meta.env.VITE_API_URL}${user.avatar_url}`
    : undefined;

  return (
    <Box
      component="nav"
      aria-label={t("sidebar.mainNav")}
      sx={{
        width: 56,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 1.5,
        gap: 0.5,
        background: "linear-gradient(180deg, #111827 0%, #0f172a 100%)",
        borderRight: "1px solid rgba(59, 130, 246, 0.1)",
        boxShadow:
          "4px 0 24px rgba(0, 0, 0, 0.5), inset -1px 0 0 rgba(59, 130, 246, 0.1)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "150px",
          background:
            "linear-gradient(180deg, rgba(59, 130, 246, 0.08) 0%, transparent 100%)",
          pointerEvents: "none",
        },
      }}
    >
      {/* Logo */}
      <Tooltip title="Glambot Studio" placement="right" arrow>
        <Box
          component={Link}
          to="/projects"
          sx={{
            width: 40,
            height: 40,
            mb: 0.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "10px",
            textDecoration: "none",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.08)",
            },
          }}
        >
          <Box
            component="img"
            src="/logo-dark.svg"
            alt="Glambot Studio"
            sx={{ width: 32, height: 32 }}
          />
        </Box>
      </Tooltip>

      {/* Org switcher */}
      <Tooltip
        title={currentOrganization?.name || "Organization"}
        placement="right"
        arrow
      >
        <IconButton
          onClick={onOrgMenuOpen}
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            color: "rgba(156, 163, 175, 0.9)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.08)",
              color: "#fff",
            },
          }}
        >
          <Avatar
            alt={currentOrganization?.name}
            src={
              currentOrganization?.logo_full_url ||
              currentOrganization?.logo_url
            }
          />
        </IconButton>
      </Tooltip>

      <Divider
        sx={{
          width: 32,
          borderColor: "rgba(59, 130, 246, 0.15)",
          my: 0.5,
        }}
      />

      {/* Main nav */}
      <NavItem
        icon={<Inventory fontSize="small" />}
        label={t("sidebar.projects")}
        to="/projects"
        isActive={isActive("/projects")}
      />

      {currentOrganization && (
        <>
          <NavItem
            icon={<Settings fontSize="small" />}
            label={t("sidebar.settings")}
            to={`/organizations/${currentOrganization.id}`}
            isActive={
              currentPath === `/organizations/${currentOrganization.id}`
            }
          />
          <NavItem
            icon={<People fontSize="small" />}
            label={t("sidebar.members")}
            to={`/organizations/${currentOrganization.id}/members`}
            isActive={
              currentPath === `/organizations/${currentOrganization.id}/members`
            }
          />
          <NavItem
            icon={<Webhook fontSize="small" />}
            label={t("sidebar.webhooks")}
            to={`/organizations/${currentOrganization.id}/webhooks`}
            isActive={
              currentPath ===
              `/organizations/${currentOrganization.id}/webhooks`
            }
          />
        </>
      )}

      <NavItem
        icon={<Payment fontSize="small" />}
        label={t("sidebar.billing")}
        to="/billing"
        isActive={isActive("/billing", true)}
      />

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Admin section */}
      {user?.is_superuser && (
        <>
          <Divider
            sx={{
              width: 32,
              borderColor: "rgba(59, 130, 246, 0.15)",
              my: 0.5,
            }}
          />
          <NavItem
            icon={<AdminPanelSettings fontSize="small" />}
            label={t("sidebar.admin.organizations")}
            to="/admin/organizations"
            isActive={isActive("/admin/organizations")}
          />
          <NavItem
            icon={<People fontSize="small" />}
            label={t("sidebar.admin.users", "Users")}
            to="/admin/users"
            isActive={isActive("/admin/users")}
          />
        </>
      )}

      {/* Resources */}
      <Divider
        sx={{
          width: 32,
          borderColor: "rgba(59, 130, 246, 0.15)",
          my: 0.5,
        }}
      />
      <NavItem
        icon={<Download fontSize="small" />}
        label={t("sidebar.download")}
        to="/download"
        isActive={isActive("/download", true)}
      />
      <NavItem
        icon={<Help fontSize="small" />}
        label={t("sidebar.help")}
        to="/help"
        isActive={isActive("/help", true)}
      />

      {/* Language selector */}
      <Tooltip title={t("sidebar.language")} placement="right" arrow>
        <IconButton
          onClick={(e) => setLangAnchor(e.currentTarget)}
          aria-label={t("sidebar.language")}
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            color: "rgba(156, 163, 175, 0.9)",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.08)",
              color: "#fff",
            },
          }}
        >
          <Translate fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={langAnchor}
        open={Boolean(langAnchor)}
        onClose={() => setLangAnchor(null)}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
      >
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            selected={currentLanguage === lang.code}
            onClick={() => {
              changeLanguage(lang.code);
              setLangAnchor(null);
            }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {currentLanguage === lang.code ? <Check fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText>{lang.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Bottom section */}
      <Divider
        sx={{
          width: 32,
          borderColor: "rgba(59, 130, 246, 0.15)",
          my: 0.5,
        }}
      />

      {/* Profile */}
      <NavItem
        icon={<Person fontSize="small" />}
        label={t("sidebar.profile")}
        to="/profile"
        isActive={isActive("/profile", true)}
      />

      {/* Logout */}
      <NavItem
        icon={<Logout fontSize="small" />}
        label={t("sidebar.logout")}
        to="/logout"
        isActive={false}
      />

      {/* User avatar */}
      <Tooltip
        title={user?.full_name || user?.email || "Profile"}
        placement="right"
        arrow
      >
        <Avatar
          src={avatarUrl}
          alt={user?.full_name || "User"}
          sx={{
            width: 32,
            height: 32,
            mt: 0.5,
            fontSize: "0.8rem",
            border: "2px solid rgba(59, 130, 246, 0.3)",
          }}
        >
          {(user?.full_name || user?.email || "U").charAt(0).toUpperCase()}
        </Avatar>
      </Tooltip>
    </Box>
  );
}
